import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import { useSearchData } from 'Components/General/SearchProvider/SearchProvider'
import React, { useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import {
  ISearchCompetence,
  ISearchCertificate,
  ISearchCourse,
  ISearchDegree,
  ISearchLanguage,
  ISearchSkill,
} from 'types/searchInterfaces'
import { useTranslation } from 'react-i18next'
import colors from 'constants/colors'
import EditPopover from './SearchComponents/EditPopover'
import LanguageEditPopover from './SearchComponents/LanguageEditPopover'
import { useUser } from 'hooks'

/**
 * React functional component for displaying terms.
 *
 * @return {React.ReactElement} The grid layout for displaying terms.
 */
const TermsDisplay: React.FC = (): React.ReactElement => {
  const { searchData, setSearchData, resetSearch } = useSearchData()
  const { t } = useTranslation()
  const { features } = useUser()

  const [anchorElement, setAnchorElement] = useState<HTMLElement>()
  const [itemData, setItemData] = useState<ISearchSkill>()
  const [updatedItemData, setUpdatedItemData] = useState<ISearchSkill>()
  const [languageItem, setLanguageItem] = useState<ISearchLanguage>()
  const [updatedLanguageItem, setUpdatedLanguageItem] = useState<ISearchLanguage>()

  /**
   * Handles the click event and updates the anchor element, item, and updated item.
   *
   * @param {Event} event - The click event
   * @param {any} data - The data associated with the event
   */
  const handleClick = (event: React.MouseEvent, data: ISearchSkill) => {
    setAnchorElement(event.currentTarget as HTMLElement)
    setItemData(data)
    setUpdatedItemData(data)
  }

  const handleLanguageClick = (event: React.MouseEvent, data: ISearchLanguage) => {
    setAnchorElement(event.currentTarget as HTMLElement)
    setLanguageItem(data)
    setUpdatedLanguageItem(data)
  }

  /**
   * Removes a skill from the searchData terms based on the kind of skill.
   *
   * @param {ISearchSkill} item - the item containing the skill to be removed
   * @return {void}
   */
  const removeSkill = (item: ISearchSkill | ISearchCertificate | ISearchCompetence) => {
    const updatedTerms = { ...searchData.terms }
    if ('skillId' in item) {
      const { kind } = item.Skill
      const { skillId } = item

      switch (kind) {
        case 'skill':
          updatedTerms.skills = updatedTerms.skills.filter(skill => skill.skillId !== skillId)
          break
        case 'industry':
          updatedTerms.industries = updatedTerms.industries.filter(industry => industry.skillId !== skillId)
          break
        case 'role':
          updatedTerms.roles = updatedTerms.roles.filter(role => role.skillId !== skillId)
          break
      }
    } else if ('certificateId' in item) {
      const { certificateId } = item

      updatedTerms.certificates = updatedTerms.certificates.filter(cert => cert.certificateId !== certificateId)
    } else if ('competenceId' in item && features.includes('competences')) {
      const { competenceId } = item

      updatedTerms.competences = updatedTerms.competences.filter(comp => comp.competenceId !== competenceId)
    }

    setSearchData({
      ...searchData,
      terms: updatedTerms,
    })

    setAnchorElement(undefined)
    setItemData(undefined)
    setUpdatedItemData(undefined)
  }

  /**
   * Removes the specified item from the search data based on the given type.
   *
   * @param {ISearchLanguage | ISearchDegree | ISearchCourse} item - the item to be removed
   * @param {string} type - the type of the item to be removed
   */
  const removeItem = (item: ISearchLanguage | ISearchDegree | ISearchCourse, type: string) => {
    const updatedTerms = { ...searchData.terms }

    switch (type) {
      case 'language':
        updatedTerms.languages = updatedTerms.languages.filter(language => language !== item)
        break

      case 'degree':
        updatedTerms.degrees = updatedTerms.degrees.filter(degree => degree !== item)
        break

      case 'course':
        updatedTerms.courses = updatedTerms.courses.filter(course => course !== item)
        break
    }

    setSearchData({
      ...searchData,
      terms: updatedTerms,
    })
  }

  /**
   * Save a skill item in the searchData state based on the item's kind.
   *
   * @param {ISearchSkill | ISearchCertificate | ISearchCompetence} item - the skill item to be saved
   * @return {void}
   */
  const saveSkill = (item: ISearchSkill | ISearchCertificate | ISearchCompetence) => {
    if ('skillId' in item) {
      let index: number
      let array: ISearchSkill[]

      switch (item.Skill.kind) {
        case 'skill':
          index = searchData.terms.skills.findIndex(skill => skill.skillId === item.skillId)
          array = searchData.terms.skills
          setSearchData({
            ...searchData,
            terms: {
              ...searchData.terms,
              skills: array.map((skill, i) => (i === index ? item : skill)),
            },
          })
          break

        case 'industry':
          index = searchData.terms.industries.findIndex(industry => industry.skillId === item.skillId)
          array = searchData.terms.industries
          setSearchData({
            ...searchData,
            terms: {
              ...searchData.terms,
              industries: array.map((industry, i) => (i === index ? item : industry)),
            },
          })
          break

        case 'role':
          index = searchData.terms.roles.findIndex(role => role.skillId === item.skillId)
          array = searchData.terms.roles
          setSearchData({
            ...searchData,
            terms: {
              ...searchData.terms,
              roles: array.map((role, i) => (i === index ? item : role)),
            },
          })
          break
      }
    } else if ('certificateId' in item) {
      const index = searchData.terms.certificates.findIndex(cert => cert.certificateId === item.certificateId)
      const array = searchData.terms.certificates
      setSearchData({
        ...searchData,
        terms: {
          ...searchData.terms,
          certificates: array.map((cert, i) => (i === index ? item : cert)),
        },
      })
    } else if ('competenceId' in item && features.includes('competences')) {
      const index = searchData.terms.competences.findIndex(comp => comp.competenceId === item.competenceId)
      const array = searchData.terms.competences
      setSearchData({
        ...searchData,
        terms: {
          ...searchData.terms,
          competences: array.map((comp, i) => (i === index ? item : comp)),
        },
      })
    }

    setAnchorElement(undefined)
    setItemData(undefined)
    setUpdatedItemData(undefined)
  }

  const saveLanguage = (item: ISearchLanguage) => {
    const index = searchData.terms.languages.findIndex(language => language.languageCode === item.languageCode)

    setSearchData({
      ...searchData,
      terms: {
        ...searchData.terms,
        languages: searchData.terms.languages.map((language, i) => (i === index ? item : language)),
      },
    })

    setAnchorElement(undefined)
    setLanguageItem(undefined)
    setUpdatedLanguageItem(undefined)
  }

  /**
   * Function to show a chip based on the type of data provided.
   *
   * @param {ISearchSkill | ISearchLanguage | ISearchCertificate | ISearchDegree | ISearchCourse | ISearchCompetence} item - the item to display as a chip
   * @param {string} type - the type of the item
   * @return {React.ReactElement} the chip element to be rendered
   */
  const showChip = (
    item: ISearchSkill | ISearchLanguage | ISearchCertificate | ISearchDegree | ISearchCourse | ISearchCompetence,
    type: string
  ): React.ReactNode => {
    if (type === 'skill') {
      const data = item as ISearchSkill
      const handleEdit = (e: React.MouseEvent) => handleClick(e, data)

      return (
        <Tooltip
          key={data.skillId}
          placement="top"
          title={
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography>
                  {t(`search.${data.Skill.kind}.name`)}: {data.name}
                </Typography>
                <Typography>
                  {t('search.level')}: {data.level ? data.level : t('search.levelNotSet')}
                </Typography>
                <Typography>
                  {t('search.experienceMonths')}:{' '}
                  {data.experienceMonths ? data.experienceMonths : t('search.experienceNotSet')}
                </Typography>
                <Typography>
                  {t('search.requiredProjects')}:{' '}
                  {data.requiredProjects ? data.requiredProjects : t('search.projectsCountNotSet')}
                </Typography>
              </Grid>
            </Grid>
          }
        >
          <Chip
            onDelete={() => removeSkill(data)}
            onClick={handleEdit}
            label={
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid
                  sx={{
                    color: colors.black,
                    fontWeight: 'bold',
                  }}
                >
                  <span
                    style={{
                      color: colors.fontNormalColor,
                      fontWeight: 'normal',
                    }}
                  >
                    {t(`search.${data.Skill.kind}.name`)}
                  </span>{' '}
                  {data.name}
                </Grid>
                <Grid>
                  <EditIcon color="newPrimary" fontSize="inherit" />
                </Grid>
              </Grid>
            }
            sx={{
              mr: 0.5,
              borderRadius: 0.5,
              backgroundColor: colors.chipBackground,
              color: colors.black,
              fontWeight: 'bold',
            }}
            size="small"
          />
        </Tooltip>
      )
    } else if (type === 'language') {
      const data = item as ISearchLanguage

      return (
        <Tooltip
          key={data.languageCode}
          placement="top"
          title={
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography>
                  {t(`search.language`)}: {data.name}
                </Typography>
                <Typography>
                  {t('search.level')}: {data.level ? data.level : t('search.levelNotSet')}
                </Typography>
              </Grid>
            </Grid>
          }
        >
          <Chip
            onDelete={() => removeItem(data, type)}
            onClick={(e: React.MouseEvent) => handleLanguageClick(e, data)}
            label={
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid
                  sx={{
                    color: colors.black,
                    fontWeight: 'bold',
                  }}
                >
                  <span
                    style={{
                      color: colors.fontNormalColor,
                      fontWeight: 'normal',
                    }}
                  >
                    {t(`search.language`)}
                  </span>{' '}
                  {data.name}
                </Grid>
                <Grid>
                  <EditIcon color="newPrimary" fontSize="inherit" />
                </Grid>
              </Grid>
            }
            sx={{
              mr: 0.5,
              borderRadius: 0.5,
              backgroundColor: colors.chipBackground,
              color: colors.black,
              fontWeight: 'bold',
            }}
            size="small"
          />
        </Tooltip>
      )
    } else if (type === 'certificate') {
      const data = item as ISearchCertificate

      return (
        <Tooltip
          key={data.certificateId}
          placement="top"
          title={
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography>
                  {t(`search.certificate`)}: {data.certificate}
                </Typography>
              </Grid>
            </Grid>
          }
        >
          <Chip
            onDelete={() => removeSkill(data)}
            label={
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid
                  sx={{
                    color: colors.black,
                    fontWeight: 'bold',
                  }}
                >
                  <span
                    style={{
                      color: colors.fontNormalColor,
                      fontWeight: 'normal',
                    }}
                  >
                    {t(`search.certificate.name`)}
                  </span>{' '}
                  {data.certificate}
                </Grid>
              </Grid>
            }
            sx={{
              mr: 0.5,
              borderRadius: 0.5,
              backgroundColor: colors.chipBackground,
              color: colors.black,
              fontWeight: 'bold',
            }}
            size="small"
          />
        </Tooltip>
      )
    } else if (type === 'competence' && features.includes('competences')) {
      const data = item as ISearchCompetence

      return (
        <Tooltip
          key={data.competenceId}
          placement="top"
          title={
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography>
                  {t(`search.competence`)}: {data.name}
                </Typography>
              </Grid>
            </Grid>
          }
        >
          <Chip
            onDelete={() => removeSkill(data)}
            label={
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid
                  sx={{
                    color: colors.black,
                    fontWeight: 'bold',
                  }}
                >
                  <span
                    style={{
                      color: colors.fontNormalColor,
                      fontWeight: 'normal',
                    }}
                  >
                    {t(`search.competence.name`)}
                  </span>{' '}
                  {data.name}
                </Grid>
              </Grid>
            }
            sx={{
              mr: 0.5,
              borderRadius: 0.5,
              backgroundColor: colors.chipBackground,
              color: colors.black,
              fontWeight: 'bold',
            }}
            size="small"
          />
        </Tooltip>
      )
    } else {
      const handleDelete = (data: ISearchDegree | ISearchCourse, itemType: string) => () => {
        removeItem(data, itemType)
      }

      let data: ISearchDegree | ISearchCourse

      if (type === 'course') {
        data = item as ISearchCourse
      } else {
        data = item as ISearchDegree
      }

      const educationValue =
        (data as { degree?: string; course?: string }).degree ?? (data as { degree?: string; course?: string }).course

      return (
        <Tooltip
          key={educationValue}
          placement="top"
          title={
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography>
                  {t(`search.${type}`)}: {educationValue}
                </Typography>
              </Grid>
            </Grid>
          }
        >
          <Chip
            onDelete={handleDelete(data, type)}
            label={
              <Grid
                sx={{
                  color: colors.black,
                  fontWeight: 'bold',
                }}
              >
                <span
                  style={{
                    color: colors.fontNormalColor,
                    fontWeight: 'normal',
                  }}
                >
                  {t(`search.${type}`)}
                </span>{' '}
                {educationValue}
              </Grid>
            }
            sx={{
              mr: 0.5,
              borderRadius: 0.5,
              backgroundColor: colors.chipBackground,
              color: colors.black,
              fontWeight: 'bold',
            }}
            size="small"
          />
        </Tooltip>
      )
    }
  }

  const { terms } = searchData

  // Checking if any term has a value
  const hasTerms = Object.values(terms).filter(term => (Array.isArray(term) ? term.length > 0 : term)).length > 0

  return (
    <Grid container justifyContent="space-between" mb={1} size={12}>
      <Grid size={10}>
        {terms.name && (
          <Chip
            label={
              <>
                <span
                  style={{
                    color: colors.fontNormalColor,
                    fontWeight: 'normal',
                  }}
                >
                  {t(`search.name`)}
                </span>{' '}
                {terms.name}
              </>
            }
            onDelete={() =>
              setSearchData({
                ...searchData,
                terms: {
                  ...terms,
                  name: '',
                },
              })
            }
            sx={{
              mr: 0.5,
              borderRadius: 0.5,
              backgroundColor: colors.chipBackground,
              color: colors.black,
              fontWeight: 'bold',
            }}
            size="small"
          />
        )}
        {terms.location && (
          <Chip
            label={
              <>
                <span
                  style={{
                    color: colors.fontNormalColor,
                    fontWeight: 'normal',
                  }}
                >
                  {t(`search.location`)}
                </span>{' '}
                {terms.location}
              </>
            }
            onDelete={() =>
              setSearchData({
                ...searchData,
                terms: {
                  ...terms,
                  location: '',
                },
              })
            }
            sx={{
              mr: 0.5,
              borderRadius: 0.5,
              backgroundColor: colors.chipBackground,
              color: colors.black,
              fontWeight: 'bold',
            }}
            size="small"
          />
        )}
        {terms.languages.length > 0 && terms.languages.map(language => showChip(language, 'language'))}
        {[...terms.skills, ...terms.roles, ...terms.industries].map(skill => showChip(skill, 'skill'))}
        {terms.certificates.length > 0 && terms.certificates.map(certificate => showChip(certificate, 'certificate'))}
        {features.includes('competences') &&
          terms.competences.length > 0 &&
          terms.competences.map(competence => showChip(competence, 'competence'))}
        {terms.degrees.length > 0 && terms.degrees.map(degree => showChip(degree, 'degree'))}
        {terms.courses.length > 0 && terms.courses.map(course => showChip(course, 'course'))}
        {itemData && updatedItemData && (
          <EditPopover
            item={itemData}
            anchorEl={anchorElement}
            updatedItem={updatedItemData}
            setUpdatedItem={setUpdatedItemData}
            saveItem={saveSkill}
            onClose={() => {
              setItemData(undefined)
              setUpdatedItemData(undefined)
              setAnchorElement(undefined)
            }}
          />
        )}
        {languageItem && updatedLanguageItem && (
          <LanguageEditPopover
            item={languageItem}
            anchorEl={anchorElement}
            updatedItem={updatedLanguageItem}
            setUpdatedItem={setUpdatedLanguageItem}
            saveItem={saveLanguage}
            onClose={() => {
              setItemData(undefined)
              setUpdatedItemData(undefined)
              setAnchorElement(undefined)
            }}
          />
        )}
      </Grid>
      <Grid size="auto">
        {hasTerms && (
          <Link
            sx={{
              color: colors.newPrimary,
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            underline="hover"
            onClick={() => resetSearch()}
          >
            {t('search.reset')}
          </Link>
        )}
      </Grid>
    </Grid>
  )
}

export default TermsDisplay
